import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input, Label } from "@/components/ui/Input";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

interface LabelRow {
  sb_enumeration: string;
  mv_enumeration: string;
  display_label: string;
  megaventory_api_enumeration: string;
  qbo_sales_term_id: string | null;
  qbo_sales_term_name: string | null;
  updated_at: string;
}

export function MegaventoryPaymentTermsLabels() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";
  const labelsQuery = useQuery({
    queryKey: ["user-admin", "megaventory-payment-term-labels"],
    queryFn: () => apiFetch<{ rows: LabelRow[] }>("/api/user-admin/megaventory-payment-term-labels"),
  });

  const [newEnumeration, setNewEnumeration] = useState("");
  const [newDisplayLabel, setNewDisplayLabel] = useState("");

  const [editDrafts, setEditDrafts] = useState<Record<string, string>>({});

  function draftDisplayLabel(row: LabelRow): string {
    const draft = editDrafts[row.sb_enumeration];
    if (draft !== undefined) return draft;
    return row.display_label;
  }

  function setDraftDisplayLabel(mvEnumeration: string, value: string): void {
    setEditDrafts((current) => ({
      ...current,
      [mvEnumeration]: value,
    }));
  }

  const invalidateAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["user-admin", "megaventory-payment-term-labels"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "megaventory-payment-term-labels"] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/user-admin/megaventory-payment-term-labels", {
        method: "POST",
        body: {
          sb_enumeration: newEnumeration.trim(),
          display_label: newDisplayLabel.trim(),
        },
      }),
    onSuccess: () => {
      invalidateAll();
      setNewEnumeration("");
      setNewDisplayLabel("");
    },
  });

  const saveMutation = useMutation({
    mutationFn: (input: { sb_enumeration: string; display_label: string }) =>
      apiFetch(`/api/user-admin/megaventory-payment-term-labels/${encodeURIComponent(input.sb_enumeration)}`, {
        method: "PUT",
        body: {
          display_label: input.display_label,
        },
      }),
    onSuccess: (_data, variables) => {
      invalidateAll();
      setEditDrafts((current) => {
        const next = { ...current };
        delete next[variables.sb_enumeration];
        return next;
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (mvEnumeration: string) =>
      apiFetch(`/api/user-admin/megaventory-payment-term-labels/${encodeURIComponent(mvEnumeration)}`, {
        method: "DELETE",
      }),
    onSuccess: (_data, mvEnumeration) => {
      invalidateAll();
      setEditDrafts((current) => {
        const next = { ...current };
        delete next[mvEnumeration];
        return next;
      });
    },
  });

  const rows = labelsQuery.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Canonical Supabase payment_terms keys (sb_enumeration), Megaventory API enums, display labels for hub dropdowns, and optional QuickBooks Online SalesTerm ids. Changes apply after save; refresh other browser tabs if needed.">
          Payment terms reference
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Existing labels</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {labelsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : labelsQuery.isError ? (
            <p className="text-sm text-rose-700">{(labelsQuery.error as Error).message}</p>
          ) : (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
            <fieldset
              disabled={!hubWritesAllowed}
              className="border-0 p-0 min-w-0 space-y-4"
            >
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Supabase key</th>
                    <th className="px-3 py-2">Megaventory API</th>
                    <th className="px-3 py-2">Display label</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {rows.map((row) => {
                    const displayDraft = draftDisplayLabel(row);
                    return (
                      <tr key={row.sb_enumeration}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-800">{row.sb_enumeration}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">
                          {row.megaventory_api_enumeration}
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={displayDraft}
                            onChange={(event) => setDraftDisplayLabel(row.sb_enumeration, event.target.value)}
                            className="h-8 text-sm"
                            aria-label={`Display label for ${row.sb_enumeration}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-2">
                            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                            <Button
                              type="button"
                              size="sm"
                              isLoading={saveMutation.isPending}
                              onClick={() => {
                                saveMutation.mutate({
                                  sb_enumeration: row.sb_enumeration,
                                  display_label: displayDraft.trim(),
                                });
                              }}
                              disabled={!displayDraft.trim() || !hubWritesAllowed}
                            >
                              Save
                            </Button>
                            </ReadOnlyHoverLayer>
                            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="text-rose-700"
                              isLoading={deleteMutation.isPending}
                              disabled={!hubWritesAllowed}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    `Delete label mapping for "${row.sb_enumeration}"? Documents that still use this key will show the raw value until you add it again.`,
                                  )
                                ) {
                                  return;
                                }
                                deleteMutation.mutate(row.sb_enumeration);
                              }}
                            >
                              Delete
                            </Button>
                            </ReadOnlyHoverLayer>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </fieldset>
            </ReadOnlyHoverLayer>
          )}
          {saveMutation.error ? (
            <p className="text-sm text-rose-700">{(saveMutation.error as Error).message}</p>
          ) : null}
          {deleteMutation.error ? (
            <p className="text-sm text-rose-700">{(deleteMutation.error as Error).message}</p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add mapping</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
          <fieldset
            disabled={!hubWritesAllowed || createMutation.isPending}
            className="space-y-3 border-0 p-0"
          >
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor="new-mv-enumeration">Megaventory enumeration</Label>
              <Input
                id="new-mv-enumeration"
                value={newEnumeration}
                onChange={(event) => setNewEnumeration(event.target.value)}
                placeholder="e.g. CustomPaymentTerms3"
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label htmlFor="new-display-label">Display label</Label>
              <Input
                id="new-display-label"
                value={newDisplayLabel}
                onChange={(event) => setNewDisplayLabel(event.target.value)}
                placeholder="e.g. Net 30"
                className="mt-1"
              />
            </div>
          </div>
          </fieldset>
          </ReadOnlyHoverLayer>
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
          <Button
            type="button"
            size="sm"
            isLoading={createMutation.isPending}
            disabled={!newEnumeration.trim() || !newDisplayLabel.trim() || !hubWritesAllowed}
            onClick={() => createMutation.mutate()}
          >
            Add mapping
          </Button>
          </ReadOnlyHoverLayer>
          {createMutation.error ? (
            <p className="text-sm text-rose-700">{(createMutation.error as Error).message}</p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
